import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useCMS } from '../../context/CMSContext';
import { Save, Sparkles, Play, RefreshCw, Plus, Trash2 } from 'lucide-react';

const AdminAIDemoSettingsPage = () => {
  const { getAIDemoSettings, updateAIDemoSettings } = useCMS();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_badge: '',
    demo_categories: [],
    cta_title: '',
    cta_subtitle: '',
    cta_button_text: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getAIDemoSettings();
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to load AI Demo settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAIDemoSettings(settings);
      toast.success('AI Demo settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (index, field, value) => {
    const newCategories = [...settings.demo_categories];
    newCategories[index] = { ...newCategories[index], [field]: value };
    setSettings(prev => ({ ...prev, demo_categories: newCategories }));
  };

  const addCategory = () => {
    const newCategory = {
      id: `category-${Date.now()}`,
      name: 'New Demo',
      icon: 'Sparkles',
      description: 'Demo description'
    };
    setSettings(prev => ({
      ...prev,
      demo_categories: [...prev.demo_categories, newCategory]
    }));
  };

  const removeCategory = (index) => {
    setSettings(prev => ({
      ...prev,
      demo_categories: prev.demo_categories.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-tech-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy">AI Demo Page Settings</h1>
          <p className="text-gray-500">Configure the AI demo page content and categories</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-tech-blue hover:bg-tech-blue/90">
          {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
          Save Changes
        </Button>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-tech-blue" />
            Hero Section
          </CardTitle>
          <CardDescription>Configure the main heading and description</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Hero Badge</label>
            <Input
              value={settings.hero_badge}
              onChange={(e) => handleChange('hero_badge', e.target.value)}
              placeholder="Live AI Demo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hero Title</label>
            <Input
              value={settings.hero_title}
              onChange={(e) => handleChange('hero_title', e.target.value)}
              placeholder="Experience AI-Powered NDIS Management"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hero Subtitle</label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-tech-blue/20"
              rows={3}
              value={settings.hero_subtitle}
              onChange={(e) => handleChange('hero_subtitle', e.target.value)}
              placeholder="Try our intelligent features..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Demo Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-care-teal" />
            Demo Categories
          </CardTitle>
          <CardDescription>Configure the available demo types</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.demo_categories?.map((category, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-3 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="font-medium">Category {index + 1}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCategory(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">ID</label>
                  <Input
                    value={category.id}
                    onChange={(e) => handleCategoryChange(index, 'id', e.target.value)}
                    placeholder="compliance"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input
                    value={category.name}
                    onChange={(e) => handleCategoryChange(index, 'name', e.target.value)}
                    placeholder="Compliance Check"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Icon (Lucide)</label>
                  <Input
                    value={category.icon}
                    onChange={(e) => handleCategoryChange(index, 'icon', e.target.value)}
                    placeholder="Shield"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Input
                    value={category.description}
                    onChange={(e) => handleCategoryChange(index, 'description', e.target.value)}
                    placeholder="AI-powered compliance monitoring"
                  />
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addCategory} className="w-full">
            <Plus className="h-4 w-4 mr-2" /> Add Demo Category
          </Button>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle>Call to Action Section</CardTitle>
          <CardDescription>Configure the bottom CTA section</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">CTA Title</label>
            <Input
              value={settings.cta_title}
              onChange={(e) => handleChange('cta_title', e.target.value)}
              placeholder="Ready to Transform Your Operations?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Subtitle</label>
            <Input
              value={settings.cta_subtitle}
              onChange={(e) => handleChange('cta_subtitle', e.target.value)}
              placeholder="See how these AI features work with your actual data"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Button Text</label>
            <Input
              value={settings.cta_button_text}
              onChange={(e) => handleChange('cta_button_text', e.target.value)}
              placeholder="Book a Personalised Demo"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAIDemoSettingsPage;
